<template>
  <div
    v-click-outside="closeDropdown"
    class="v2-filter-delivery-date"
  >
    <v2-button
      :variant="isActive ? 'primary' : 'outline'"
      @click="toggleDropdown"
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
      </svg>
      Filter By Delivery Date
      <span
        v-if="selectedDates.length"
        class="v2-filter-delivery-date__badge"
      >
        {{ selectedDates.length }}
      </span>
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline :points="showDropdown ? '18 15 12 9 6 15' : '6 9 12 15 18 9'" />
      </svg>
    </v2-button>
    <div
      v-if="showDropdown"
      class="v2-filter-delivery-date__dropdown"
    >
      <div class="v2-filter-delivery-date__header">
        <h4 class="v2-heading-xs v2-m-0">
          Filter By Delivery Date
        </h4>
      </div>
      <div class="v2-filter-delivery-date__content">
        <div class="v2-filter-delivery-date__label">
          Delivery Window
        </div>
        <v2-button
          variant="outline"
          class="v2-filter-delivery-date__select-btn"
          @click="toggleDateList"
        >
          Select Delivery Window Dates
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline :points="showDateList ? '18 15 12 9 6 15' : '6 9 12 15 18 9'" />
          </svg>
        </v2-button>
        <div
          v-if="showDateList"
          class="v2-filter-delivery-date__list"
        >
          <template v-if="availabilityDates.length">
            <div
              v-for="(date, index) in availabilityDates"
              :key="index"
              class="v2-filter-delivery-date__item"
              @click="toggleDateSelection(index)"
            >
              <input
                type="checkbox"
                :checked="selectedDates.includes(index)"
                class="v2-filter-delivery-date__checkbox"
              >
              <span>{{ date }}</span>
            </div>
          </template>
          <template v-else>
            <div class="v2-filter-delivery-date__empty">
              No Dates Available
            </div>
          </template>
        </div>
        <template v-if="selectedDates.length && !showDateList">
          <div class="v2-filter-delivery-date__selected-label">
            {{ selectedDates.length }} Delivery {{ selectedDates.length > 1 ? 'Windows' : 'Window' }} Selected:
          </div>
          <div class="v2-filter-delivery-date__selected-list">
            <div
              v-for="dateIndex in selectedDates"
              :key="dateIndex"
              class="v2-filter-delivery-date__selected-item"
            >
              {{ availabilityDates[dateIndex] }}
              <button
                class="v2-filter-delivery-date__remove-btn"
                @click="removeDateSelection(dateIndex)"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <line
                    x1="18"
                    y1="6"
                    x2="6"
                    y2="18"
                  />
                  <line
                    x1="6"
                    y1="6"
                    x2="18"
                    y2="18"
                  />
                </svg>
              </button>
            </div>
          </div>
        </template>
      </div>
      <v2-button
        variant="primary"
        class="v2-filter-delivery-date__apply-btn"
        :disabled="!selectedDates.length"
        @click="applyFilters"
      >
        Apply Filters
      </v2-button>
    </div>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import V2Button from './V2Button.vue'

export default {
  name: 'V2FilterByDeliveryDate',
  components: {
    V2Button,
  },
  directives: {
    clickOutside: {
      bind(el, binding) {
        el.clickOutsideEvent = event => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event)
          }
        }
        document.addEventListener('click', el.clickOutsideEvent)
      },
      unbind(el) {
        document.removeEventListener('click', el.clickOutsideEvent)
      },
    },
  },
  props: {
    availabilityDates: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const showDropdown = ref(false)
    const showDateList = ref(false)
    const selectedDates = ref([...props.value])

    const toggleDropdown = () => {
      showDropdown.value = !showDropdown.value
      if (showDropdown.value) {
        showDateList.value = false
      }
    }

    const closeDropdown = () => {
      showDropdown.value = false
      showDateList.value = false
    }

    const toggleDateList = () => {
      showDateList.value = !showDateList.value
    }

    const toggleDateSelection = index => {
      const idx = selectedDates.value.indexOf(index)
      if (idx > -1) {
        selectedDates.value.splice(idx, 1)
      } else {
        selectedDates.value.push(index)
      }
    }

    const removeDateSelection = index => {
      const idx = selectedDates.value.indexOf(index)
      if (idx > -1) {
        selectedDates.value.splice(idx, 1)
      }
    }

    const applyFilters = () => {
      emit('input', [...selectedDates.value])
      emit('apply', [...selectedDates.value])
      showDropdown.value = false
      showDateList.value = false
    }

    const isActive = ref(props.value.length > 0)

    return {
      showDropdown,
      showDateList,
      selectedDates,
      isActive,
      toggleDropdown,
      closeDropdown,
      toggleDateList,
      toggleDateSelection,
      removeDateSelection,
      applyFilters,
    }
  },
  watch: {
    value: {
      handler(newVal) {
        this.selectedDates = [...newVal]
        this.isActive = newVal.length > 0
      },
      deep: true,
    },
  },
}
</script>

<style lang="scss" scoped>
// Bootstrap functions/variables/mixins (no CSS output) — gives media-breakpoint-*.
@import '~@core/scss/base/bootstrap-extended/include';

.v2-filter-delivery-date {
  position: relative;

  // Below the desktop breakpoint this filter is rendered inside the wishlist
  // filter bottom-sheet, where the absolute popover would overflow the narrow
  // sheet. There, the trigger and panel size to 280px and the panel flows
  // in-line (see the &__dropdown rule). The desktop header keeps the popover
  // behaviour (it's hidden on mobile). The dropdown's mobile override lives
  // inside the &__dropdown block itself so it wins the cascade over the base
  // `position: absolute` (a sibling @media here would lose by source order).
  // Breakpoint via Bootstrap's mixin (md → ≤991.98px) instead of a raw query.
  @include media-breakpoint-down(md) {
    display: block;
    width: 100%;

    & > .v2-button {
      width: 280px;
      max-width: 100%;
      justify-content: space-between;
    }
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.25rem;
    height: 1.25rem;
    padding: 0 0.25rem;
    border-radius: 999px;
    background: var(--v2-destructive);
    color: white;
    font-size: 0.625rem;
    font-weight: 700;
    margin-left: 0.25rem;
  }

  &__dropdown {
    position: absolute;
    top: calc(100% + 0.5rem);
    left: 0;
    z-index: 1000;
    min-width: 280px;
    // Floating glass — near-opaque white so date options stay readable when
    // the panel opens over the page mesh / product cards / dark imagery.
    background: var(--v2-glass-surface-floating);
    backdrop-filter: blur(var(--v2-glass-blur-md)) saturate(180%);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-lg);
    box-shadow: var(--v2-glass-shadow-floating);
    padding: var(--v2-space-4);

    // Mobile filter sheet: flow in-line (not a floating popover) at a fixed
    // 280px, capped to the sheet width on very narrow phones. Nested here (after
    // `position: absolute`) so it wins the cascade.
    @include media-breakpoint-down(md) {
      position: static;
      width: 280px;
      max-width: 100%;
      min-width: 0;
      margin-top: var(--v2-space-2);
    }
  }

  &__header {
    margin-bottom: var(--v2-space-3);
    padding-bottom: var(--v2-space-3);
    border-bottom: 1px solid var(--v2-border);
  }

  &__content {
    margin-bottom: var(--v2-space-3);
  }

  &__label {
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
    margin-bottom: var(--v2-space-2);
  }

  &__select-btn {
    width: 100%;
    justify-content: space-between;
    margin-bottom: var(--v2-space-2);
  }

  &__list {
    max-height: 200px;
    overflow-y: auto;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    margin-bottom: var(--v2-space-2);
  }

  &__item {
    display: flex;
    align-items: center;
    padding: var(--v2-space-2) var(--v2-space-3);
    cursor: pointer;
    border-bottom: 1px solid var(--v2-border);
    transition: background 0.15s ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: var(--v2-muted);
    }
  }

  &__checkbox {
    margin-right: var(--v2-space-2);
    accent-color: var(--v2-primary);
    cursor: pointer;
  }

  &__empty {
    padding: var(--v2-space-4);
    text-align: center;
    color: var(--v2-muted-foreground);
    font-size: 0.875rem;
  }

  &__selected-label {
    font-size: 0.875rem;
    color: var(--v2-foreground);
    margin-bottom: var(--v2-space-2);
  }

  &__selected-list {
    max-height: 150px;
    overflow-y: auto;
    margin-bottom: var(--v2-space-2);
  }

  &__selected-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--v2-space-2) var(--v2-space-3);
    background: var(--v2-secondary);
    border-radius: var(--v2-radius-sm);
    font-size: 0.875rem;
    margin-bottom: var(--v2-space-1);
  }

  &__remove-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.125rem;
    background: none;
    border: none;
    color: var(--v2-muted-foreground);
    cursor: pointer;
    border-radius: var(--v2-radius-sm);

    &:hover {
      color: var(--v2-destructive);
      background: rgba(229, 72, 77, 0.1);
    }
  }

  &__apply-btn {
    width: 100%;
  }
}
</style>
